import { type FC, useRef } from "react";
import { useInView } from "react-intersection-observer";
import { Button } from "@root/shared/components/Button";
import * as styles from "./styles.css";

type Props = {
  currentWorksCount: number;
  isAnimating: boolean;
  isSaveButtonVisible: boolean;
  onSearch: () => void;
  onSave: () => void;
};

const BOTTOM_OFFSET_PX = 80;

export const SearchActions: FC<Props> = ({
  currentWorksCount,
  isAnimating,
  isSaveButtonVisible,
  onSearch,
  onSave,
}) => {
  /**
   * NOTE: フッター内のアコーディオン（非React）を開閉するとページ高さが変動し、
   * position: fixed への切り替え時に要素がドキュメントフローから外れることで
   * さらに高さが変わり、IntersectionObserver の判定がループして追従ボタンが
   * 激しくチラつく問題があった。
   * 対策として fixed 時にプレースホルダー（同じ高さの空div）を挿入し、
   * ページ高さを維持することで Observer の判定を安定させている。
   */
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { ref: inViewRef, inView } = useInView({
    rootMargin: `0px 0px -${BOTTOM_OFFSET_PX}px 0px`,
  });
  const isFixed = !inView;
  const isSearchDisabled = currentWorksCount === 0;

  return (
    <>
      <div ref={inViewRef} />
      {isFixed && wrapperRef.current && <div style={{ height: wrapperRef.current.offsetHeight }} />}
      <div ref={wrapperRef} className={styles.wrapper({ isFixed })}>
        <div className={styles.worksCount({ isFixed })}>
          <span className={styles.resultLabel({ isFixed })}>絞り込み結果</span>
          <span>
            <span className={styles.worksCountNumber({ isAnimating })}>{currentWorksCount}</span>件
          </span>
        </div>
        <div className={styles.buttonArea({ isFixed })}>
          {!isFixed && isSaveButtonVisible && (
            <Button theme="secondary" type="button" onClick={onSave} className={styles.saveButton}>
              この検索条件を保存する
            </Button>
          )}
          <Button
            theme="primary"
            type="button"
            onClick={onSearch}
            className={styles.searchButton({ isFixed })}
            data-ga-action={isFixed ? "search_form_fixed" : "search_form_under"}
            {...(isSearchDisabled && { disabled: true })}
          >
            検索する
          </Button>
        </div>
      </div>
    </>
  );
};
